import React, { useState } from 'react';
import { LLMIcon } from '../../../../assets/images';
import BasicModal from '../../../../components/action-modal';
import makeToast from '../../../../components/toaster';
import { validateTextField } from '../../../../services';
import { createEmbeddedProvider } from '../services/embedded-llm';
import { addEmbeddedProviderProps, embeddedProviderDetailsProps } from '../embedded.props';
import {
  AddProviderDesc,
  AddProviderText,
  ProviderKeyText,
  ProviderNameText,
} from '../data/constants';
import { PROVIDER_SELECT_OPTIONS } from '../../llm/pages/data/constants';
import { FilterDropdown } from '../../../../components';

function AddEmbeddedProvider({
  openProviderModal,
  setOpenProviderModal,
  setDataUpdated,
}: addEmbeddedProviderProps) {
  const [providerDetails, setProviderDetails] = useState<embeddedProviderDetailsProps>({
    name: '',
    key: '',
  });
  const [errorMsgs, setErrorsMsgs] = useState<embeddedProviderDetailsProps>({
    name: '',
    key: '',
  });

  const handleCancelClick = () => {
    setProviderDetails({
      name: '',
      key: '',
    });
    setErrorsMsgs({
      name: '',
      key: '',
    });
    setOpenProviderModal(false);
  };

  const handleSaveProvider = () => {
    const { name, key } = providerDetails;
    const nameError = validateTextField(name, 'Provider Name');
    const keyError = validateTextField(key, 'API Key');
    if (nameError || keyError) {
      setErrorsMsgs({ name: nameError, key: keyError });
      return;
    }
    createEmbeddedProvider(providerDetails)
      .then((res) => {
        if (res.status === 201) {
          handleCancelClick();
          setDataUpdated((prev) => !prev);
          makeToast('success', res.data.message);
        } else if (res.status === 401) {
          // Handle logout if needed
        }
      })
      .catch((err) => {
        makeToast(
          'error',
          err?.response?.data?.message
            ? `${err?.response?.data?.message}.`
            : 'Something went wrong!',
        );
      });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const error =
      value === ''
        ? `${name === 'name' ? 'Provider Name' : 'API Key'} is required!`
        : validateTextField(value, name === 'name' ? 'Provider Name' : 'API Key');
    setErrorsMsgs({ ...errorMsgs, [name]: error });
    setProviderDetails({ ...providerDetails, [name]: value });
  };

  return (
    <BasicModal
      className="w-[550px]"
      confirmButtonText="Save"
      open={openProviderModal}
      modalTitle={AddProviderText}
      modalDesc={AddProviderDesc}
      modalLogo={LLMIcon}
      resetData={handleCancelClick}
      saveData={handleSaveProvider}
    >
      <div>
        <div className="flex flex-col text-sm">
          <span className="text-[#344054] font-medium">
            {ProviderNameText}
            <span className="text-red-600">*</span>
          </span>
          <FilterDropdown
            options={PROVIDER_SELECT_OPTIONS.map((option) => ({
              value: option,
              label: option,
            }))}
            value={providerDetails?.name ?? ''}
            onChange={(v: string) =>
              handleChange({
                target: { name: 'name', value: v },
              } as React.ChangeEvent<HTMLInputElement>)
            }
            placeholder="Select provider"
            showClear={false}
            className={`mt-1 w-full px-3 py-2 text-left flex items-center justify-between rounded-lg border bg-white ${
              errorMsgs.name !== ''
                ? 'error-msg border-red-600 focus:ring-red-600'
                : 'border-[#E4E4E7]'
            }`}
          />
          {errorMsgs?.name && <span className="text-red-600">{errorMsgs?.name}</span>}
        </div>
        <div className="flex flex-col text-sm mt-4">
          <span className="text-[#344054] font-medium">
            {ProviderKeyText}
            <span className="text-red-600">*</span>
          </span>
          <input
            className={`mt-1 text-sm ${
              errorMsgs?.key ? 'focus:border-red-600 border-red-600' : 'border-[#E4E4E7]'
            } rounded-lg w-full`}
            value={providerDetails?.key}
            name="key"
            required
            placeholder="Enter API key"
            onChange={(e) => handleChange(e)}
          />
          {errorMsgs.key && <span className="text-red-600">{errorMsgs?.key}</span>}
        </div>
      </div>
    </BasicModal>
  );
}

export default AddEmbeddedProvider;
